/**
 * Dealer and prevailing-wind rotation between rounds.
 *
 * Deliberately NOT part of the pure scoring engine (`src/rules/`) — this is
 * about table conduct (who deals next), not about scoring a hand. It stays
 * pure anyway so it can be unit tested and so `deriveTableState` can replay
 * it deterministically from `game.rounds` without any extra persisted state.
 */

import type { Wind } from '../rules/tiles';
import type { Game } from '../game/types';

const WIND_ORDER: Wind[] = ['east', 'south', 'west', 'north'];

export interface TableState {
  /** Index into `game.players` (turn order around the table). */
  dealerIndex: number;
  prevailingWind: Wind;
  /** Dealer changes since the prevailing wind last advanced (0..3). */
  dealerRotations: number;
}

export function initialTableState(): TableState {
  return { dealerIndex: 0, prevailingWind: 'east', dealerRotations: 0 };
}

export function seatWindOf(playerIndex: number, dealerIndex: number): Wind {
  return WIND_ORDER[(playerIndex - dealerIndex + 4) % 4]!;
}

export type RoundOutcome =
  | { type: 'win'; winnerIndex: number }
  | { type: 'draw' };

/**
 * The dealer retains the seat on a win by the dealer, or on any draw.
 * Otherwise the seat passes to the next player in turn order. Every 4 dealer
 * changes, the prevailing wind advances one step.
 */
export function nextTableState(table: TableState, outcome: RoundOutcome): TableState {
  const dealerRetains =
    outcome.type === 'draw' ||
    (outcome.type === 'win' && outcome.winnerIndex === table.dealerIndex);

  if (dealerRetains) return table;

  const dealerRotations = table.dealerRotations + 1;
  const dealerIndex = (table.dealerIndex + 1) % 4;

  if (dealerRotations >= 4) {
    const windIdx = WIND_ORDER.indexOf(table.prevailingWind);
    return {
      dealerIndex,
      prevailingWind: WIND_ORDER[(windIdx + 1) % 4]!,
      dealerRotations: 0,
    };
  }
  return { dealerIndex, prevailingWind: table.prevailingWind, dealerRotations };
}

/**
 * Reconstructs the current table state by replaying every recorded round
 * from the start of the game. Cheap at game-session scale, and it means
 * dealer/wind tracking needs no storage of its own — `game.rounds` is
 * already the source of truth.
 */
export function deriveTableState(game: Game): TableState {
  let table = initialTableState();
  for (const round of game.rounds) {
    const outcome: RoundOutcome =
      round.outcome === 'win' && round.winner
        ? { type: 'win', winnerIndex: game.players.findIndex((p) => p.id === round.winner) }
        : { type: 'draw' };
    table = nextTableState(table, outcome);
  }
  return table;
}
